(() => {
  if (customElements.get('cart-shipping-rates')) return;
  let queue = Promise.resolve();
  const storageKey = 'dpd-shipping-destination-v1';
  class CartShippingRates extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.country = this.querySelector('[data-rate-country]');
      this.province = this.querySelector('[data-rate-province]');
      this.zip = this.querySelector('[data-rate-zip]');
      this.button = this.querySelector('[data-rate-calculate]');
      this.status = this.querySelector('[data-rate-status]');
      this.list = this.querySelector('[data-rate-list]');
      let saved = {};
      try { saved = JSON.parse(sessionStorage.getItem(storageKey)) || {}; } catch {}
      const desired = saved.country || this.dataset.country;
      const option = Array.from(this.country.options).find(o => o.value === desired || o.textContent === desired);
      if (option) this.country.value = option.value;
      this.updateProvinces(saved.province || this.dataset.province);
      this.zip.value = saved.zip || this.dataset.zip || '';
      this.country.addEventListener('change', () => { this.updateProvinces(); this.invalidate(); });
      this.province.addEventListener('change', () => this.invalidate());
      this.zip.addEventListener('input', () => this.invalidate());
      this.button.addEventListener('click', () => this.calculate());
      this.addEventListener('keydown', event => {
        if (event.key === 'Enter' && event.target.matches('input,select')) {
          event.preventDefault();
          this.calculate();
        }
      });
      if (this.dataset.required === 'false') {
        this.hidden = true;
        return;
      }
      if (this.zip.value.trim() && this.country.value !== '---' && (!this.province.options.length || this.province.value)) this.calculate();
    }
    disconnectedCallback() { this.version = (this.version || 0) + 1; }
    updateProvinces(selected = '') {
      let provinces = [];
      try { provinces = JSON.parse(this.country.selectedOptions[0]?.dataset.provinces || '[]'); } catch {}
      this.province.replaceChildren();
      if (provinces.length) this.province.add(new Option('—', ''));
      for (const [value, label] of provinces) this.province.add(new Option(label, value));
      this.province.value = selected;
      this.querySelector('[data-rate-province-field]').hidden = !provinces.length;
      this.province.disabled = !provinces.length;
    }
    invalidate() {
      this.version = (this.version || 0) + 1;
      this.status.textContent = this.dataset.idle;
      this.list.replaceChildren();
      this.list.hidden = true;
      this.button.disabled = false;
      this.removeAttribute('aria-busy');
    }
    async calculate() {
      if (this.button.disabled) return;
      const address = {country:this.country.value, province:this.province.value, zip:this.zip.value.trim()};
      if (!address.country || address.country === '---' || !address.zip || (!this.province.disabled && !address.province)) {
        this.status.textContent = this.dataset.validation;
        this.querySelector('details').open = true;
        return;
      }
      try { sessionStorage.setItem(storageKey, JSON.stringify(address)); } catch {}
      const version = this.version = (this.version || 0) + 1;
      const current = () => this.isConnected && this.version === version;
      this.button.disabled = true;
      this.setAttribute('aria-busy', 'true');
      this.status.textContent = this.dataset.loading;
      this.list.hidden = true;
      this.list.replaceChildren();
      const task = async () => {
        if (!current()) return;
        const params = new URLSearchParams();
        for (const [key, value] of Object.entries(address)) if (value) params.set('shipping_address[' + key + ']', value);
        const root = window.Shopify?.routes?.root || '/';
        const options = {cache:'no-store', headers:{Accept:'application/json'}, signal:AbortSignal.timeout(20000)};
        const prepared = await fetch(root + 'cart/prepare_shipping_rates.json?' + params, {...options, method:'POST'});
        if (!prepared.ok) throw new Error('Shipping calculation failed');
        for (let attempt = 0; attempt < 12; attempt++) {
          if (!current()) return;
          const response = await fetch(root + 'cart/async_shipping_rates.json?' + params, {...options, signal:AbortSignal.timeout(15000)});
          if (!response.ok) throw new Error('Shipping rates unavailable');
          const data = await response.json();
          if (!current()) return;
          if (data && Array.isArray(data.shipping_rates)) {
            const rates = data.shipping_rates.filter(rate => Number.isFinite(Number(rate.price)) && Number(rate.price) >= 0);
            if (!rates.length) { this.status.textContent = this.dataset.empty; return; }
            rates.sort((a,b) => Number(a.price) - Number(b.price));
            this.status.textContent = [address.zip, this.country.selectedOptions[0]?.textContent, this.province.selectedOptions[0]?.textContent].filter(Boolean).join(' · ');
            for (const rate of rates) {
              const item = document.createElement('li');
              const name = document.createElement('span');
              const price = document.createElement('strong');
              name.textContent = rate.presentment_name || rate.name;
              price.textContent = Number(rate.price) === 0 ? this.dataset.free : new Intl.NumberFormat(this.dataset.locale || document.documentElement.lang, {style:'currency',currency:rate.currency || this.dataset.currency}).format(Number(rate.price));
              item.append(name, price);
              this.list.append(item);
            }
            this.list.hidden = false;
            return;
          }
          await new Promise(resolve => setTimeout(resolve, Math.min(500 * (attempt + 1), 2000)));
        }
        throw new Error('Shipping calculation timed out');
      };
      const job = queue.then(task);
      queue = job.catch(() => {});
      try { await job; }
      catch { if (current()) this.status.textContent = this.dataset.error; }
      finally { if (current()) { this.button.disabled = false; this.removeAttribute('aria-busy'); } }
    }
  }
  customElements.define('cart-shipping-rates', CartShippingRates);
})();
